import { ControllerHelper, PostController, QuestionBucketDownloadDto, QuestionDownloadDto } from 'collaboration-service';
import LoadingDataTable, { ColumnDefintion } from 'components/LoadingDataTable/LoadingDataTable';
import { Button, getUriFromLinkByName, Icon, Image } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'services/ApplicationState/ApplicationState';
import { shallowCompare } from 'services/Helpers';
import { getTranslatedStandardDate } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';

const mapper = (s: ApplicationState) => ({
    params: s.params,
});
interface EditQuizBucketProps {
    bucket: QuestionBucketDownloadDto;
    index: number;
    replaceBucket: (i: number, bucket?: QuestionBucketDownloadDto) => void;
}

const EditQuizBucket = (p: EditQuizBucketProps) => {
    const { bucket, index, replaceBucket } = p;
    const { t } = useImgI18N("admin");
    const { params } = useSelector(mapper, shallowCompare);

    const groupId = params?.groupid as string;

    const [editMode, setEditMode] = React.useState<boolean>(false);
    const [selectMode, setSelectMode] = React.useState<boolean>(false);
    const [questions, setQuestions] = React.useState<QuestionDownloadDto[]>([]);

    React.useEffect(() => {
        const load = async () => {
            const res = await ControllerHelper.singleCall({ groupid: groupId, fromdate: 'all', todate: 'all', onlyvisible: 'Ignore' }, PostController.GetFromTo, true);
            if (res) {
                const pp: QuestionDownloadDto[] = [];
                _.forEach(_.filter(res.elements, po => po.type !== "Quiz"), po => {
                    pp.push(po as QuestionDownloadDto);
                });
                setQuestions(pp);
            }
        };
        if (groupId) {
            load();
        }
    }, [groupId]);
    const toggleEditMode = React.useMemo(() => () => { setEditMode(!editMode); }, [editMode]);
    const toggleSelectMode = React.useMemo(() => () => { setSelectMode(!selectMode); }, [selectMode]);

    const qColummns = React.useMemo((): ColumnDefintion<QuestionDownloadDto>[] => [

        {
            elementName: "headlines",
            width: 3,
            align: "left",
            columnCreator: "lngString",
        },
        {
            elementName: "categories",
            width: 2,
            align: "left",
            columnCreator: "categories",
        },
        {
            elementName: "type",
            width: 4,
            align: "left",
            filterFn: "text",
        },
        {
            elementName: "media",
            width: 2,
            align: "left",
            cell: (cell) => <Image
                src={getUriFromLinkByName(cell.row.original.media, "self") ?? ''}
                width="60px"
                squared
                thumbnail
                borderColor={"@veryLightGrey"}
                floated="left"
            />,
        },
        {
            elementName: "descriptions",
            width: 6,
            align: "left",
            columnCreator: "lngString",
        },
    ], []);

    const columns = React.useMemo((): ColumnDefintion<QuestionDownloadDto>[] => [
        {
            elementName: "hidden",
            width: 1,
            align: "left",
            cell: p => {
                const data = p.row.original;
                return (
                    <div>
                        <Icon name={data.hidden ? 'checkbox checked' : 'checkbox unchecked'} />
                    </div>
                )
            }
        },
        {
            elementName: "headlines",
            width: 3,
            align: "left",
            columnCreator: "lngString",
        },
        {
            elementName: "categories",
            width: 2,
            align: "left",
            columnCreator: "categories",
        },
        {
            elementName: "type",
            width: 4,
            align: "left",
            filterFn: "text",
        },
        {
            elementName: "media",
            width: 2,
            align: "left",
            cell: (cell) => <Image
                src={getUriFromLinkByName(cell.row.original.media, "self") ?? ''}
                width="60px"
                squared
                thumbnail
                borderColor={"@veryLightGrey"}
                floated="left"
            />,
        },
        {
            elementName: "descriptions",
            width: 6,
            align: "left",
            columnCreator: "lngString",
        },
        {
            elementName: "publicationDate",
            width: 2,
            align: "left",
            accessor: p => getTranslatedStandardDate(p?.publicationDate ?? new Date(), "PP"),
        },
        {
            elementName: "expirationDate",
            width: 2,
            align: "left",
            accessor: p => getTranslatedStandardDate(p?.expirationDate ?? new Date(), "PP"),
        },
        {
            elementName: "created",
            width: 2,
            align: "left",
            accessor: p => getTranslatedStandardDate(p.created, "PP"),
        },
        {
            elementName: "changed",
            width: 2,
            align: "left",
            accessor: p => getTranslatedStandardDate(p.changed, "PP"),
        },
    ], []);

    const onDelete = React.useMemo(() => (question: QuestionDownloadDto) => {
        const idx = _.findIndex(bucket.questions, q => q.id === question.id);
        if (idx >= 0) {
            const copy = _.cloneDeep(bucket);
            copy.questions.splice(idx, 1);
            replaceBucket(index, copy);
        }

    }, [bucket, index, replaceBucket]);

    const subComponent = React.useMemo(() => (question: QuestionDownloadDto) => {
        return <div>
            {question.id}
        </div>;
    }, []);

    const onSelectChanged = React.useMemo(() => (question: QuestionDownloadDto) => {
        const copy = _.cloneDeep(bucket);
        copy.questions.push(question);
        replaceBucket(index, copy);
    }, [bucket, index, replaceBucket]);

    return (
        <div>
            <div style={{ display: "flex", alignItems: "left", marginTop: 5, gap: 5 }} >
                <Button icon='delete' kind='primary' tooltip={{ tooltipText: t('delete this bucket permanently') }} onClick={() => replaceBucket(index)} />
                <Button icon='edit' kind='secondary' tooltip={{ tooltipText: t('edit bucket') }} onClick={toggleEditMode} />
                <div style={{ marginTop: 10, marginLeft: 10, fontWeight: 'bold', fontSize: 16 }}>

                    {'bucket nr.' + (index + 1)}
                </div>
            </div>
            {editMode &&
                <div>
                    <Button icon='bomb' kind='halfTransparentButton' tooltip={{ tooltipText: t('get selection of available questions and add desired question to the bucket') }} onClick={toggleSelectMode} />
                    {selectMode &&
                        <div>
                            <div style={{ fontSize: 16, color: 'green', textAlign: 'center' }}>
                                {'to add one of questions to the bucket below, click the button on the left side of desired question. use filter options to find desired question.'}
                            </div>
                            <LoadingDataTable
                                loadData={questions}
                                loaderData={{}}
                                columns={qColummns}
                                actionButtons={[
                                    {
                                        icon: 'select',
                                        kind: 'halfTransparentButton',
                                        tooltip: { tooltipText: t('select question for bucket below') },
                                        onClick: onSelectChanged,
                                    },
                                ]}
                            />
                        </div>
                    }
                    <LoadingDataTable
                        loadData={bucket.questions}
                        loaderData={{}}
                        noFilter
                        columns={columns}
                        mayDelete
                        renderSubComponent={subComponent}
                        actionButtons={[
                            {
                                icon: 'delete row',
                                kind: 'transparentButton',
                                tooltip: { tooltipText: t('remove question') },
                                onClick: onDelete,
                            },
                        ]}
                    />
                </div>
            }
        </div>
    );
}
export default EditQuizBucket;